import React, { useCallback, useContext, useEffect, useState } from "react";
import { commitMutation, graphql, useFragment } from "react-relay";
import { useHistory } from "react-router-dom";

import { useFeatureIsOn, useFeatureValue } from "@growthbook/growthbook-react";

import tailwindBase from "~/css/base.css?url";
import semantic from "~/css/semantic.css?url";
import legacySemantic from "~/css/semantic/legacy-semantic.css?url";
import textVariablesCompat from "~/css/text-variables-compat.css?url";
import textVariables from "~/css/text-variables.css?url";
import legacyTextStyles from "~/ui/legacy-text-styles.css?url";

import type { uxSimplificationFeature_useSyncUXSimplificationWithRelay$key } from "./__generated__/uxSimplificationFeature_useSyncUXSimplificationWithRelay.graphql";
import type { uxSimplificationFeatureLatestRecordingQuery } from "./__generated__/uxSimplificationFeatureLatestRecordingQuery.graphql";
import type { uxSimplificationFeatureToggleUXSimplificationMutation as ToggleUXSimplificationMutation } from "./__generated__/uxSimplificationFeatureToggleUXSimplificationMutation.graphql";
import { useLazyLoadQuery } from "./lib/relayUtil";
import { environment } from "./models";
import { EarlyAccessModal } from "./settings/SettingsModal/EarlyAccess";
import { AnchorButton, useModals } from "./ui";
import toast from "./ui/toast";

function useToggleStylesheet(url: string, enabled: boolean) {
	useEffect(() => {
		const link = document.querySelector<HTMLLinkElement>(`link[href="${url}"]`);

		if (!link) {
			console.warn(`Stylesheet link not found for url: ${url}`);
			return;
		}

		link.disabled = !enabled;
	}, [url, enabled]);
}

/**
 * This is to support the other UX simplification hooks. You need to use the toggle in `useIsUXSimplificationEnabled` instead.
 *
 * @example
 * ```tsx
 * const { toggleUXSimplification } = useIsUXSimplificationEnabled();
 * ```
 */
type StylesheetsContextValueType = (enabled: boolean) => void;

const StylesheetsContext = React.createContext<StylesheetsContextValueType | null>(null);

export function StylesheetsProvider({ children }: { children: React.ReactNode }) {
	const [isUXSimplificationEnabled, setUXSimplificationStylesEnabled] = useState(
		document.documentElement.dataset.uxSimplification === "enabled",
	);

	// New stylesheets
	useToggleStylesheet(semantic, isUXSimplificationEnabled);
	useToggleStylesheet(textVariables, isUXSimplificationEnabled);
	useToggleStylesheet(tailwindBase, isUXSimplificationEnabled);

	// Old stylesheets
	useToggleStylesheet(legacySemantic, !isUXSimplificationEnabled);
	useToggleStylesheet(legacyTextStyles, !isUXSimplificationEnabled);
	useToggleStylesheet(textVariablesCompat, !isUXSimplificationEnabled);

	useEffect(() => {
		document.documentElement.dataset.uxSimplification = isUXSimplificationEnabled ? "enabled" : "disabled";
	}, [isUXSimplificationEnabled]);

	return <StylesheetsContext.Provider value={setUXSimplificationStylesEnabled}>{children}</StylesheetsContext.Provider>;
}

export function useSetUXSimplificationStylesheets(): StylesheetsContextValueType {
	const context = useContext(StylesheetsContext);

	if (!context) {
		throw new Error("useSetUXSimplificationStylesheets must be used within a StylesheetsProvider");
	}

	return context;
}

const latestRecordingQuery = graphql`
	query uxSimplificationFeatureLatestRecordingQuery {
		viewer {
			recordingsAndClips(first: 1, entityType: recordings) {
				edges {
					node {
						... on MeetingRecording {
							id
							url
						}
					}
				}
			}
		}
	}
`;

const useLatestRecordingUrl = () => {
	const { viewer } = useLazyLoadQuery<uxSimplificationFeatureLatestRecordingQuery>(
		latestRecordingQuery,
		{},
		{ fetchPolicy: "network-only" },
	);

	return viewer?.recordingsAndClips?.edges?.[0]?.node?.url ?? "/";
};

export function useUXSimplificationFeature(): {
	/** This is the growthbook flag. You need to use `viewer.user.uxSimplificationEnabled` instead. */
	isUXSimplificationFeatureFlagEnabled: boolean;
	toggleUXSimplification: (enabled: boolean, options?: { skipRedirect: boolean }) => void;
} {
	const setUXSimplificationStylesEnabled = useSetUXSimplificationStylesheets();
	const isUXSimplificationFeatureFlagEnabled = useFeatureIsOn("ux-simplification");
	const inviteLink = useFeatureValue("early-access-slack-link", "");

	const latestRecordingUrl = useLatestRecordingUrl();

	const history = useHistory();
	const modals = useModals();

	const toggleUXSimplification = useCallback(
		(checked: boolean, options?: { skipRedirect: boolean }) => {
			commitMutation<ToggleUXSimplificationMutation>(environment, {
				mutation: graphql`
					mutation uxSimplificationFeatureToggleUXSimplificationMutation($enabled: Boolean!) {
						setUxSimplificationEnabled(enabled: $enabled) {
							viewer {
								uxSimplificationEnabled
							}
						}
					}
				`,
				variables: {
					enabled: checked,
				},
				onCompleted: (_, error) => {
					if (error) {
						console.error("Failed to toggle UX simplification", error);
						return;
					}

					setUXSimplificationStylesEnabled(checked);

					if (checked && !options?.skipRedirect) {
						history.push(latestRecordingUrl);
						modals.open(({ close }) => <EarlyAccessModal onClose={close} />);
					} else if (!checked && !options?.skipRedirect) {
						toast.info("New interface not working out for you? Let us know why", {
							actions: (
								<AnchorButton variant="primary" href={inviteLink} target="_blank">
									Give feedback
								</AnchorButton>
							),
							closeButton: true,
							autoClose: false,
						});
					}
				},
			});
		},
		[setUXSimplificationStylesEnabled, history, latestRecordingUrl, modals, inviteLink],
	);

	return { isUXSimplificationFeatureFlagEnabled, toggleUXSimplification } as const;
}

export function useSyncUXSimplificationWithRelay(
	queryKey: uxSimplificationFeature_useSyncUXSimplificationWithRelay$key,
) {
	const query = useFragment(
		graphql`
			fragment uxSimplificationFeature_useSyncUXSimplificationWithRelay on Query {
				viewer {
					uxSimplificationEnabled
				}
			}
		`,
		queryKey,
	);

	const setUXSimplificationStylesEnabled = useSetUXSimplificationStylesheets();
	setUXSimplificationStylesEnabled(query.viewer.uxSimplificationEnabled);
}
